import React, { ReactNode } from 'react'
import Layout from 'antd/lib/layout'
import notification from 'antd/lib/notification'
import { BREAKPOINTS, STATUS } from '@features/General'
import flattenDeep from 'lodash/flattenDeep'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { AppDispatch, RootState } from '@app-store/store'
import Header from '@components/Layouts/Header'
import ControllerFooter from '@components/ControllerFooter'
import routes from '@utils/constants/routes'
import { useWindowSize } from 'hooks'
import styles from './index.scss'
import { rootActions } from '@app-store/slices'
import { useDispatch, useSelector } from 'react-redux'
import { history } from '@components/App'
import MobileNavigation from '@components/Navigation/MobileNavigation'
interface IDatabaseImportLayout {
    title: string
    children: ReactNode
    subTitle?: ReactNode
    widthPage?: string
    importAttendees?: boolean
}

function DatabaseImportLayout(props: IDatabaseImportLayout) {
    const { width } = useWindowSize()
    const { children, title, widthPage = '100%', subTitle = null, importAttendees = false } = props
    const state = useSelector((state: RootState) => state)
    const { data: i18ns } = useSelector((state: RootState) => state.localization.locals)
    const dispatch = useDispatch()

    return (
        <>
            {/* --- mobile sidebar navigation  --- */}
            {width <= BREAKPOINTS.MD ? <MobileNavigation /> : null}
            <Layout className={styles['step-layout']}>
                {/* --- header  --- */}
                <Header layoutMode="step-layout" />
                {/* --- content  --- */}
                <Layout.Content className={styles['step-layout__content']} style={{ width: widthPage }}>
                    <div className={styles['step-layout__title']}>
                        <h1>{title}</h1>
                        <div>{!isNil(subTitle) ? subTitle : null}</div>
                    </div>
                    {children}
                </Layout.Content>
                {/* --- footer  --- */}
                <ControllerFooter
                    leaveAction={() => {
                        dispatch(rootActions.dataImport.flow.reset())
                        history.push(routes.settings.event._)
                    }}
                    keyRoute={routes.database_import._}
                    backRoute={importAttendees ? routes.settings.event.import._ : ''}
                    steps={makeSteps(state, dispatch, i18ns)}
                />
            </Layout>
        </>
    )
}

const findRequiredMaps = (supportedNames, excelMap) => {
    const entries = Object.entries(supportedNames)
        .map((item: any[]) =>
            item[1]
                .filter(z => z.required)
                .map(x => ({
                    key: item[0],
                    ...x
                }))
        )
        .filter(y => y.length > 0)

    let errors = [...flattenDeep(entries)]
    excelMap.forEach(mapItem => {
        const itemIndex = errors.findIndex(
            element => element.name === mapItem.value && element.key === mapItem.groupName
        )
        if (itemIndex >= 0) {
            errors = errors.filter((item, index) => index !== itemIndex)
        }
    })

    return errors
}

const makeSteps = (state: RootState, dispatch: AppDispatch, i18ns: any) => {
    const flow = state.dataImport.flow
    const uploadFile = state.dataImport.fileUpload
    const finalizeImport = state.dataImport.finalizeImport
    const initiateImport = state.dataImport.initiateImport
    const excelMap = state.dataImport.flow.excelMap
    const supportedNames = state.dataImport.initiateImport.data.supportedNames
    const requestInitiateImport = () => dispatch(rootActions.dataImport.initiateImport.onRequest('upload'))
    const requestFinalizeImport = () => {
        const requiresNotFilled = findRequiredMaps(supportedNames, excelMap)
        if (isEmpty(requiresNotFilled)) {
            dispatch(rootActions.dataImport.finalizeImport.onRequest())
        } else {
            notification.error({
                message:
                    i18ns?.TradeHub?.You_not_matched_require_fields ||
                    'You have not matched all require fields. please match:',
                description: `${requiresNotFilled.map(item => `${item.title} of ${item.key}`).join(', ')}`,
                duration: 15000
            })
        }
    }
    return [
        {
            key: 'file',
            routes: [
                { route: 'file/files', skip: false, back: true, next: { disabled: isEmpty(flow.filesCount) } },
                { route: 'file/objects', skip: false, back: true, next: { disabled: isEmpty(flow.objectsCount) } }
            ],
            stepText: i18ns?.TradeHub?.File
        },
        {
            key: 'entity',
            routes: [{ route: 'entity', skip: false, back: true, next: { disabled: isEmpty(flow.objects) } }],
            stepText: i18ns?.TradeHub?.Entity
        },
        {
            key: 'upload',
            routes: [
                {
                    route: 'upload',
                    skip: false,
                    back: true,
                    next: {
                        onClick: requestInitiateImport,
                        isFetching: initiateImport.status === STATUS.RUNNING,
                        disabled:
                            isEmpty(flow.upload.batchName) ||
                            isNil(flow.upload.file) ||
                            uploadFile.status === STATUS.RUNNING ||
                            initiateImport.status === STATUS.RUNNING
                    }
                }
            ],
            stepText: i18ns?.General?.Upload
        },
        {
            key: 'map-columns',
            routes: [
                {
                    route: 'map-columns',
                    skip: false,
                    back: true,
                    next: {
                        disabled: isEmpty(flow.excelMap) || finalizeImport.status === STATUS.RUNNING,
                        isFetching: finalizeImport.status === STATUS.RUNNING,
                        onClick: requestFinalizeImport
                    }
                }
            ],
            stepText: i18ns?.TradeHub?.Map_Columns
        },
        {
            key: 'complete',
            routes: [
                {
                    route: 'complete',
                    skip: false,
                    back: false,
                    next: {
                        text: i18ns?.Settings?.Finish,
                        onClick: () => {
                            history.push(routes.settings.event._)
                            dispatch(rootActions.dataImport.flow.reset())
                        }
                    }
                }
            ],
            stepText: i18ns?.General?.Complete
        }
    ]
}

export default React.memo(DatabaseImportLayout)
